import {useMemo} from 'react';
import {kr, tickLabels} from '../../lib/format.js';
import {Defs, FILLS, fillById, niceScale, Grid, useHover, HitBands, Tooltip, Legend}
	from './primitives.jsx';

const W = 560, H = 300, L = 56, R = 10, T = 10, B = 26;
const MAX_SERIES = 7;

/**
 * Stacked bars over an arbitrary keyed map on each month — articles, or accounts.
 *
 * The series list is derived from the VISIBLE window, ranked by total, and everything
 * past the seventh is folded into "Övriga". Beyond about eight fills the textures stop
 * being distinguishable, and a monochrome palette has no ninth hue to reach for — so
 * the tail is folded rather than drawn in a colour nobody can name.
 *
 * Ranking by the visible window rather than by all time is deliberate: pick Q1 2025
 * and you get the seven that mattered in Q1 2025, not the seven that dominate 2026.
 */
export default function StackedByKey({title, hint, months, field, names, unit = 'kr'}) {
	const {at, enter, leave} = useHover();
	const ticks = useMemo(() => tickLabels(months), [months]);

	const {series, bars, y, x, sc} = useMemo(() => {
		const totals = new Map();
		for (const m of months) {
			for (const [k, v] of Object.entries(m[field] ?? {})) {
				totals.set(k, (totals.get(k) ?? 0) + (v ?? 0));
			}
		}
		const ranked = [...totals.entries()].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
		const top = ranked.slice(0, MAX_SERIES).map(([k]) => k);
		const rest = new Set(ranked.slice(MAX_SERIES).map(([k]) => k));
		const series = [
			...top.map((k, i) => ({key: k, label: names?.[k] ? `${k} ${names[k]}` : k,
				fill: FILLS[i % FILLS.length].id})),
			...(rest.size ? [{key: '__rest', label: `Övriga (${rest.size})`,
				fill: FILLS[MAX_SERIES % FILLS.length].id}] : []),
		];

		const stacked = months.map(m => {
			const src = m[field] ?? {};
			const row = {};
			for (const k of top) row[k] = src[k] ?? 0;
			if (rest.size) {
				row.__rest = Object.entries(src)
					.filter(([k]) => rest.has(k)).reduce((s, [, v]) => s + (v ?? 0), 0);
			}
			return row;
		});

		let hi = 0, lo = 0;
		for (const row of stacked) {
			let up = 0, dn = 0;
			for (const s of series) { const v = row[s.key] ?? 0; v >= 0 ? (up += v) : (dn += v); }
			hi = Math.max(hi, up); lo = Math.min(lo, dn);
		}
		const sc = niceScale(lo, hi);
		const iw = W - L - R, ih = H - T - B;
		const y = v => T + ih - ((v - sc.lo) / (sc.hi - sc.lo)) * ih;
		const x = i => L + (iw * (i + 0.5)) / months.length;
		const bw = Math.max(2, Math.min(26, (iw / months.length) * 0.62));

		const bars = stacked.flatMap((row, i) => {
			let up = 0, dn = 0;
			return series.map(s => {
				const v = row[s.key] ?? 0;
				if (!v) return null;
				const h = (Math.abs(v) / (sc.hi - sc.lo)) * ih;
				if (h < 0.4) return null;
				const yy = v > 0 ? y(up + v) : y(dn);
				if (v > 0) up += v; else dn += v;
				return {id: `${months[i].month}-${s.key}`, x: x(i) - bw / 2, y: yy, w: bw,
					h: Math.max(h - (bw > 6 ? 1 : 0), 0.6), ...fillById(s.fill)};
			}).filter(Boolean);
		});
		return {series, bars, y, x, sc};
	}, [months, field, names]);

	return (
		<div className="chart">
			<div className="chart__head">
				<span className="chart__title">{title}</span>
				<span className="chart__hint">{hint}</span>
			</div>
			<div className="chart__canvas">
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
					aria-label={title}>
					<Defs />
					<Grid {...sc} y={y} L={L} R={R} W={W} />
					{bars.map(({id, x: bx, y: by, w, h, ...paint}) => (
						<rect key={id} x={bx} y={by} width={w} height={h} rx={w > 6 ? 2 : 0} {...paint} />
					))}
					{ticks.map((t, i) => (t == null ? null : (
						<text key={months[i].month} x={x(i)} y={H - 8} className="axs" textAnchor="middle">{t}</text>
					)))}
					<HitBands months={months} x={x} W={W} H={H} onEnter={enter} onLeave={leave} />
				</svg>
				<Tooltip months={months} at={at} x={x} W={W} title={m => m.label ?? m.month}
					rows={m => {
						const src = m[field] ?? {};
						const shown = series.filter(s => s.key !== '__rest');
						const restSum = Object.entries(src)
							.filter(([k]) => !shown.some(s => s.key === k))
							.reduce((s, [, v]) => s + (v ?? 0), 0);
						const total = Object.values(src).reduce((s, v) => s + (v ?? 0), 0);
						return [
							...shown.filter(s => src[s.key]).map(s => [s.label, `${kr(src[s.key])} ${unit}`]),
							...(restSum ? [['Övriga', `${kr(restSum)} ${unit}`]] : []),
							['Totalt', `${kr(total)} ${unit}`],
						];
					}} />
			</div>
			<Legend items={series.map(s => ({label: s.label, fill: s.fill}))} />
		</div>
	);
}
